import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { ChannelRecap as Recap } from '@avslack/shared';
import { getRecapStatus, requestChannelRecap } from '../lib/api';
import { Icon } from './Icon';

/**
 * "Összefoglaló, amíg távol voltál" - az "Új üzenetek" vonal alatt.
 *
 * Csak akkor latszik, ha az admin bekapcsolta (a szoveg kulso modellhez megy),
 * es csak a kero latja az eredmenyt. Minden pont arra az uzenetre ugrik,
 * amelyikre tamaszkodik: az osszefoglalot nem kell vakon elhinni.
 */
export function ChannelRecap({
  channelId,
  afterSeq,
  unread,
}: {
  channelId: string;
  /** A belepeskori olvasottsag: innen szamit olvasatlannak. */
  afterSeq: number;
  unread: number;
}) {
  const navigate = useNavigate();
  const { data: status } = useQuery({
    queryKey: ['recap-status'],
    queryFn: getRecapStatus,
    staleTime: 5 * 60_000,
  });
  const [recap, setRecap] = useState<Recap | null>(null);
  const request = useMutation({
    mutationFn: () => requestChannelRecap(channelId, afterSeq),
    onSuccess: setRecap,
  });

  if (!status?.enabled) return null;

  if (!recap) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={request.isPending}
          onClick={() => request.mutate()}
          className="flex items-center gap-1.5 rounded-pill border border-accent/40 bg-accent-soft px-3 py-1 text-xs font-semibold text-accent transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <Icon name="atirat" size="sm" />
          {request.isPending
            ? 'Összefoglaló készül…'
            : `Összefoglaló, amíg távol voltál (${unread})`}
        </button>
        {request.isError && (
          <span role="alert" className="text-xs text-danger">
            {request.error instanceof Error
              ? request.error.message
              : 'Az összefoglaló most nem készült el.'}
          </span>
        )}
      </div>
    );
  }

  return (
    <section
      aria-label="Összefoglaló az olvasatlan üzenetekről"
      className="mt-2 rounded-card border border-accent/30 bg-accent-soft/40 px-3.5 py-2.5"
    >
      <div className="mb-1 flex items-center gap-2">
        <p className="text-xs font-semibold text-accent">
          Összefoglaló · {recap.messageCount} üzenetből{recap.truncated ? ' (az elsőkből)' : ''}
        </p>
        <button
          type="button"
          onClick={() => setRecap(null)}
          aria-label="Összefoglaló bezárása"
          className="ml-auto text-ink-faint hover:text-ink"
        >
          <Icon name="bezaras" size="sm" />
        </button>
      </div>
      {recap.points.length === 0 ? (
        <p className="text-sm text-ink-muted">Nem volt miből összefoglalót írni.</p>
      ) : (
        <ul className="space-y-1">
          {recap.points.map((point, index) => (
            <li key={index} className="flex gap-2 text-sm text-ink">
              <span aria-hidden="true" className="text-ink-faint">
                •
              </span>
              {point.messageId ? (
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: '/c/$channelId',
                      params: { channelId },
                      search: { m: point.messageId! },
                    })
                  }
                  className="text-left hover:underline"
                  title="Ugrás az üzenetre"
                >
                  {point.text}
                </button>
              ) : (
                <span>{point.text}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1.5 text-2xs text-ink-faint">
        Gépi összefoglaló – csak te látod. Egy pontra kattintva az üzenethez ugrik.
      </p>
    </section>
  );
}
